// src/components/Dashboard/SystemHealthView.tsx
import { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Storage,
  Api,
  Language,
  People,
  Folder,
} from '@mui/icons-material';
import {
  getSystemHealth,
  SystemHealthResponse,
  DashboardApiError,
} from '../../services/dashboardApi';
import { format } from 'date-fns';

export default function SystemHealthView() {
  const [health, setHealth] = useState<SystemHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false); // Default minimized

  useEffect(() => {
    const loadHealth = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getSystemHealth();
        setHealth(response);
      } catch (err) {
        const apiError = err as DashboardApiError;
        setError(apiError.message || 'Failed to load system health');
        console.error('Error loading system health:', err);
      } finally {
        setLoading(false);
      }
    };

    loadHealth();

    // Auto-refresh every 60 seconds
    const interval = setInterval(loadHealth, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!health) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="info">No system health data available</Alert>
      </Paper>
    );
  }

  const getStatusColor = (status: string): 'success' | 'error' => {
    return status === 'healthy' ? 'success' : 'error';
  };

  const getStatusIcon = (status: string) => {
    return status === 'healthy' ? <CheckCircle /> : <ErrorIcon />;
  };

  const allHealthy = health && 
    health.database.status === 'healthy' && 
    health.api.status === 'healthy' && 
    health.site.status === 'healthy';

  return (
    <Paper>
      <Box sx={{ p: 2, borderBottom: expanded ? 1 : 0, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">System Health</Typography>
          {!expanded && health && (
            <Chip
              icon={allHealthy ? <CheckCircle /> : <ErrorIcon />}
              label={allHealthy ? 'All Systems Healthy' : 'Issues Detected'}
              color={allHealthy ? 'success' : 'error'}
              size="small"
            />
          )}
        </Box>
        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      
      {/* Minimized View - Overall Status */}
      {!expanded && health && (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip
                  icon={getStatusIcon(health.database.status)}
                  label="Database"
                  color={getStatusColor(health.database.status)}
                  sx={{ mb: 1 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip
                  icon={getStatusIcon(health.api.status)}
                  label="API"
                  color={getStatusColor(health.api.status)}
                  sx={{ mb: 1 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip
                  icon={getStatusIcon(health.site.status)}
                  label="Site"
                  color={getStatusColor(health.site.status)}
                  sx={{ mb: 1 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {health.activeUsers}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active Users
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Expanded View - Full Details */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
          {/* Health Indicators */}
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Storage />
                  <Typography variant="subtitle1">Database</Typography>
                </Box>
                <Chip
                  icon={getStatusIcon(health.database.status)}
                  label={health.database.status.toUpperCase()}
                  color={getStatusColor(health.database.status)}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Last check: {format(new Date(health.database.lastCheck), 'MMM dd, HH:mm:ss')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Api />
                  <Typography variant="subtitle1">API</Typography>
                </Box>
                <Chip
                  icon={getStatusIcon(health.api.status)}
                  label={health.api.status.toUpperCase()}
                  color={getStatusColor(health.api.status)}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Last check: {format(new Date(health.api.lastCheck), 'MMM dd, HH:mm:ss')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Language />
                  <Typography variant="subtitle1">Site</Typography>
                </Box>
                <Chip
                  icon={getStatusIcon(health.site.status)}
                  label={health.site.status.toUpperCase()}
                  color={getStatusColor(health.site.status)}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Last check: {format(new Date(health.site.lastCheck), 'MMM dd, HH:mm:ss')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Metrics */}
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <People />
                  <Typography variant="subtitle1">Active Users</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {health.activeUsers}
                </Typography>
                {health.lastSuccessfulLogDate && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Last login: {format(new Date(health.lastSuccessfulLogDate), 'MMM dd, HH:mm:ss')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Folder />
                  <Typography variant="subtitle1">System Totals</Typography>
                </Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {health.systemMetrics.totalProjects}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Projects
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {health.systemMetrics.totalTasks}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Tasks
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {health.systemMetrics.totalActions}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Actions
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        </Box>
      </Collapse>
    </Paper>
  );
}

